import { Icon, Intent, Spinner, SpinnerSize, Button } from "@blueprintjs/core"
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  QueryStatus,
} from "@tanstack/react-query"
import { Dispatch, SetStateAction } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { LargeText } from "../../../../styled/text.styled"
import { Wave } from "./types/wave"
import WaveCard from "./WaveCard"
import { SpinnerContainer, WaveListContainer } from "./WaveList.styled"
import WithLoading from "../../../components/WithLoading/WithLoading"

interface ITradingProps {
  waves?: Wave[]
  selectedWave?: Wave
  setSelectedWaveId: Dispatch<SetStateAction<string | number | undefined>>
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<Wave[], unknown>>
  hasNextPage: boolean
  isLoading: boolean
  isFetchingNextPage: boolean
  status: QueryStatus
}

const WaveList = ({
  waves,
  selectedWave,
  setSelectedWaveId,
  fetchNextPage,
  hasNextPage,
  isLoading,
  isFetchingNextPage,
  status,
}: ITradingProps) => {
  /**
   * Display a placeholder if there's no PreviousWave yet.
   */
  if (!waves?.length && status === "success") {
    return (
      <WaveListContainer
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap="25px"
      >
        <Icon icon="inbox" size={100} />
        <LargeText>There are no previous Waves</LargeText>
      </WaveListContainer>
    )
  }

  const InfiniteScrollWithLoading = WithLoading(InfiniteScroll)

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  return (
    <WaveListContainer flexDirection="column" id="waves-inbox">
      <InfiniteScrollWithLoading
        isLoading={isLoading}
        dataLength={waves?.length ?? 0}
        style={{ display: "flex", flexDirection: "column" }}
        loader={
          <SpinnerContainer justifyContent="center" flexGrow={1}>
            <Spinner intent={Intent.SUCCESS} size={SpinnerSize.SMALL} />
          </SpinnerContainer>
        }
        next={fetchNextPage}
        hasMore={hasNextPage}
        scrollableTarget="waves-inbox"
        endMessage={
          <SpinnerContainer justifyContent="center" style={{ padding: "20px" }}>
            <LargeText>All waves loaded</LargeText>
          </SpinnerContainer>
        }
      >
        {waves?.map(wave => (
          <WaveCard
            key={wave.id}
            wave={wave}
            selected={wave === selectedWave}
            setSelectedWaveId={setSelectedWaveId}
          />
        ))}

        {/* Simple Load More button at end of list */}
        {hasNextPage && !isFetchingNextPage && (
          <SpinnerContainer justifyContent="center">
            <Button
              minimal
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              loading={isFetchingNextPage}
              icon="arrow-down"
            >
              Load More Waves
            </Button>
          </SpinnerContainer>
        )}
      </InfiniteScrollWithLoading>
    </WaveListContainer>
  )
}

export default WaveList
